import React, { useEffect, useState, useRef } from 'react';
import { Reveal } from './ui/Reveal';
import { Phone } from './ui/Phone';
import { GlowingEffect } from './ui/glowing-effect';

interface CaseStudyProps {
  client: string;
  description: string;
  image: string;
  videoUrl?: string; 
  stats: { label: string; value: string; sub?: string }[];
  reverse?: boolean;
  color?: string;
  className?: string;
}

const AnimatedStat: React.FC<{ value: string }> = ({ value }) => {
  // Initial state logic moved to effect to handle hydration, or simplified
  // We initialize with a "loading" state or the formatted zero immediately if possible
  const [displayValue, setDisplayValue] = useState(""); 
  const elementRef = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    // Calculate initial zero format
    const regex = /^([^0-9]*)([\d.,]+)(.*)$/;
    const match = value.match(regex);
    if (match) {
        const [, prefix, numStr, suffix] = match;
        const isCommaDecimal = numStr.includes(',') && !numStr.includes('.');
        const decimals = isCommaDecimal 
            ? (numStr.split(',')[1] || '').length 
            : (numStr.split('.')[1] || '').length;
            
        // Start with 0 formatted correctly (e.g. "0,0")
        const startNum = 0;
        let formattedStart = startNum.toFixed(decimals);
        if (isCommaDecimal) formattedStart = formattedStart.replace('.', ',');
        setDisplayValue(`${prefix}${formattedStart}${suffix}`);
    } else {
        setDisplayValue(value);
    }
  }, [value]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          animateValue();
        }
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [value]);

  const animateValue = () => {
    const regex = /^([^0-9]*)([\d.,]+)(.*)$/;
    const match = value.match(regex);

    if (!match) {
      setDisplayValue(value);
      return;
    }

    const [, prefix, numStr, suffix] = match;
    const isCommaDecimal = numStr.includes(',') && !numStr.includes('.');

    let normalizedNum = 0;
    if (isCommaDecimal) {
        normalizedNum = parseFloat(numStr.replace(',', '.'));
    } else {
        normalizedNum = parseFloat(numStr.replace(/,/g, ''));
    }

    if (isNaN(normalizedNum)) {
        setDisplayValue(value);
        return;
    }

    const decimals = isCommaDecimal 
        ? (numStr.split(',')[1] || '').length 
        : (numStr.split('.')[1] || '').length;

    const duration = 2000;
    const frameDuration = 1000 / 60; 
    const totalFrames = Math.round(duration / frameDuration);
    let frame = 0;

    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      const currentVal = normalizedNum * ease;
      let formattedVal = currentVal.toFixed(decimals);

      if (isCommaDecimal) {
        formattedVal = formattedVal.replace('.', ',');
      }

      setDisplayValue(`${prefix}${formattedVal}${suffix}`);

      if (frame === totalFrames) {
        clearInterval(counter);
        setDisplayValue(value);
      }
    }, frameDuration);
  };

  return (
    <span ref={elementRef} className="tabular-nums">
      {displayValue || "0"} 
    </span>
  );
};

export const CaseStudy: React.FC<CaseStudyProps> = ({ client, description, image, videoUrl, stats, reverse = false, className = "" }) => {
  return (
    <div className={`group relative w-full ${className}`}>
      
      {/* 
         LAYOUT CONTAINER
         Horizontal alignment with reduced gaps.
      */}
      <div className={`
        flex items-start 
        ${reverse ? 'flex-row-reverse md:flex-row-reverse' : 'flex-row md:flex-row'} 
        md:gap-8
      `}>
        
        {/* 
            VISUAL SIDE (PHONE)
            Added 'sticky top-32' and 'self-start' to keep it fixed while scrolling content
        */}
        <div className={`
            relative shrink-0 transition-all duration-500
            w-[38%] md:w-[25%]
            ${!reverse ? '-ml-6 md:ml-0' : '-mr-6 md:mr-0'}
            md:flex md:justify-center
            md:sticky md:top-32 md:self-start
        `}>
           <Reveal width="100%" direction="up">
             <div className="rotate-0 md:group-hover:scale-105 transition-transform duration-500 origin-center">
                <Phone 
                  image={image} 
                  videoUrl={videoUrl}
                  className="shadow-[0_0_20px_rgba(255,255,255,0.05)] md:shadow-[0_0_40px_rgba(255,255,255,0.08)] md:max-w-[200px]" 
                  borderless={true}
                />
             </div>
           </Reveal>
        </div>

        {/* 
            CONTENT SIDE
        */}
        <div className={`
            flex-1 flex flex-col gap-1 md:gap-3 z-10
            ${!reverse ? 'pl-3 pr-3 md:pl-0 md:pr-12' : 'pr-3 pl-3 md:pr-0 md:pl-12'}
            ${!reverse ? 'text-right md:text-left' : 'text-left md:text-left'} 
            md:text-left
            py-4 md:py-0
        `}>
          <Reveal direction="up">
            <h2 className="text-xl md:text-4xl font-bold font-['Space_Grotesk'] leading-tight uppercase tracking-tighter text-white">
              {client}
            </h2>
          </Reveal>

          <Reveal delay={0.1} direction="up" width="100%">
            <div className={`md:border-l-2 md:border-white/20 md:pl-5 ${!reverse ? 'md:ml-0' : 'md:ml-0'}`}>
                <p className="text-[11px] md:text-base text-zinc-400 leading-snug md:leading-relaxed italic opacity-90">
                {description}
                </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mt-2 md:mt-3">
             {stats.map((stat, idx) => (
               <Reveal key={idx} delay={0.2 + (idx * 0.1)} width="100%" direction="up">
                 <div className="relative h-full rounded-2xl border border-white/10 p-2 md:rounded-3xl md:p-2 bg-zinc-900/20">
                      <GlowingEffect
                          spread={40}
                          glow={true}
                          disabled={false}
                          proximity={64}
                          inactiveZone={0.01}
                          variant="white"
                      />
                      <div className="relative flex h-full flex-col justify-center overflow-hidden rounded-xl bg-zinc-950/80 p-3 md:p-4 transition-colors duration-300 hover:bg-zinc-950/60">
                          <div className="flex justify-between items-start w-full">
                              <h3 className="text-base md:text-2xl font-bold text-white mb-0.5 pr-2">
                                <AnimatedStat value={stat.value} />
                              </h3>
                              {stat.sub && (
                                  <span className="text-[9px] md:text-[10px] text-zinc-600 font-medium uppercase tracking-tighter shrink-0 pt-1">
                                      {stat.sub}
                                  </span>
                              )}
                          </div>
                          
                          <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-widest font-medium text-left mt-1">
                            {stat.label}
                          </p>
                      </div>
                 </div>
               </Reveal>
             ))}
          </div>

        </div>

      </div>
    </div>
  );
};